/**
 * Hook Orquestador de Autenticación — useAuthFlow (Paso 5.7)
 */

'use client';

import { useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import { useAuth } from '@/core/auth/auth-context';
import { ROUTES } from '@/config/routes';
import { normalizeApiError } from '@/core/exceptions/api-errors';
import { authApi } from '../api/auth.api';
import { useAuthFlowStore } from '../store/auth.store';

export function useAuthFlow() {
  const router = useRouter();
  const { login } = useAuth();
  const {
    step,
    ticketPreauth,
    telefonoOtp,
    cooldownSeconds,
    isSubmitting,
    errorMessage,
    setStep,
    setTicketPreauth,
    setTelefonoOtp,
    setCooldownSeconds,
    setIsSubmitting,
    setErrorMessage,
    resetAuthFlow,
  } = useAuthFlowStore();

  const iniciarSesionCredenciales = useCallback(
    async (identificador: string, password: string, rememberMe = true) => {
      setIsSubmitting(true);
      setErrorMessage(null);
      try {
        const res = await authApi.loginPorCredenciales({ identificador, password });
        if (res.requiere_mfa && res.ticket_preauth) {
          setTicketPreauth(res.ticket_preauth);
          setStep('MFA_CHALLENGE');
          toast.info('Ingresa el código de 6 dígitos de tu autenticador MFA');
          return res;
        }
        if (res.access_token) {
          login(
            res.access_token,
            res.refresh_token || undefined,
            {
              id: res.id_usuario || 'user',
              rol: res.rol,
              farm_id: res.id_granja || undefined,
              granja_id: res.id_granja || undefined,
            },
            rememberMe
          );
          toast.success('Bienvenido a AviFácil');
          router.push(ROUTES.GRANJA);
        }
        return res;
      } catch (err) {
        const norm = normalizeApiError(err);
        setErrorMessage(norm.message);
        toast.error(norm.message);
        return null;
      } finally {
        setIsSubmitting(false);
      }
    },
    [login, router, setErrorMessage, setIsSubmitting, setStep, setTicketPreauth]
  );

  const validarDesafioMfa = useCallback(
    async (codigoTotp: string) => {
      if (!ticketPreauth) return null;
      setIsSubmitting(true);
      setErrorMessage(null);
      try {
        const res = await authApi.verificarDesafioMfa({
          ticket_preauth: ticketPreauth,
          codigo_totp: codigoTotp,
        });
        if (res.access_token) {
          login(res.access_token, res.refresh_token || undefined, {
            id: res.id_usuario || 'user',
            rol: res.rol,
            farm_id: res.id_granja || undefined,
          });
          toast.success('Verificación de segundo factor exitosa');
          router.push(ROUTES.GRANJA);
        }
        return res;
      } catch (err) {
        const norm = normalizeApiError(err);
        setErrorMessage(norm.message);
        toast.error(norm.message);
        return null;
      } finally {
        setIsSubmitting(false);
      }
    },
    [login, router, setErrorMessage, setIsSubmitting, ticketPreauth]
  );

  const enviarOtpWhatsapp = useCallback(
    async (telefono: string) => {
      setIsSubmitting(true);
      setErrorMessage(null);
      try {
        const res = await authApi.solicitarOtpWhatsapp({ telefono_movil: telefono });
        setTelefonoOtp(telefono);
        setCooldownSeconds(res.cooldown_segundos || 60);
        setStep('OTP_VERIFY');
        toast.success(`Código enviado por WhatsApp a ${res.telefono_enmascarado}`);
        return res;
      } catch (err) {
        const norm = normalizeApiError(err);
        setErrorMessage(norm.message);
        toast.error(norm.message);
        return null;
      } finally {
        setIsSubmitting(false);
      }
    },
    [setCooldownSeconds, setErrorMessage, setIsSubmitting, setStep, setTelefonoOtp]
  );

  const confirmarOtpWhatsapp = useCallback(
    async (codigoOtp: string) => {
      setIsSubmitting(true);
      setErrorMessage(null);
      try {
        const res = await authApi.verificarOtpWhatsapp({
          telefono_movil: telefonoOtp,
          codigo_otp: codigoOtp,
        });
        if (res.access_token) {
          login(res.access_token, res.refresh_token || undefined, {
            id: res.id_usuario || 'operario',
            rol: res.rol || 'OPERARIO_GALPON',
            farm_id: res.id_granja || undefined,
          });
          toast.success('Acceso de galpón verificado');
          router.push(ROUTES.DASHBOARD.ROOT);
        }
        return res;
      } catch (err) {
        const norm = normalizeApiError(err);
        setErrorMessage(norm.message);
        toast.error(norm.message);
        return null;
      } finally {
        setIsSubmitting(false);
      }
    },
    [login, router, setErrorMessage, setIsSubmitting, telefonoOtp]
  );

  return {
    step,
    ticketPreauth,
    telefonoOtp,
    cooldownSeconds,
    isSubmitting,
    errorMessage,
    setStep,
    setCooldownSeconds,
    iniciarSesionCredenciales,
    validarDesafioMfa,
    enviarOtpWhatsapp,
    confirmarOtpWhatsapp,
    resetAuthFlow,
  };
}
